import { Box, Tooltip, Typography } from '@mui/material';
import { InfoOutlined as InfoIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';

/**
 * Precio de la verificación de antecedentes en puntos (2026-11-01,
 * PLAN_COBRO_ANTECEDENTES). Debe coincidir con SERVICE_COSTS.backgroundCheck
 * de serviceConfig.js (backend); el modal de la columna usa el que devuelve
 * el backend (pricing.points) cuando viene.
 * Se cobra solo la consulta que trae un informe nuevo de Chapi: no se cobran
 * las reutilizadas (menos de 30 días), las que fallan, los RUT sin datos ni
 * "Volver a consultar" cuando Chapi devuelve el mismo informe del mes.
 */
export const BACKGROUND_CHECK_POINTS = 0.5;

/** "0,5" (es) / "0.5" (en): hasta 2 decimales, sin ceros de más */
export const formatCheckPoints = (n, lang = i18n.language) => {
  const value = Math.round((Number(n) || 0) * 100) / 100;
  try {
    return value.toLocaleString(String(lang || 'es').startsWith('en') ? 'en-US' : 'es-CL', { maximumFractionDigits: 2 });
  } catch (e) {
    return String(value);
  }
};

/** Saldo: siempre con 2 decimales, como el indicador de puntos ("1,00") */
export const formatPointsBalance = (n, lang = i18n.language) => {
  const value = Math.round((Number(n) || 0) * 100) / 100;
  try {
    return value.toLocaleString(String(lang || 'es').startsWith('en') ? 'en-US' : 'es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  } catch (e) {
    return value.toFixed(2);
  }
};

/** Puntos de una consulta en el idioma activo (se actualiza al cambiar el idioma) */
export const useCheckPoints = (points = BACKGROUND_CHECK_POINTS) => {
  const { i18n: instance } = useTranslation();
  const lang = instance.language;
  return { points: formatCheckPoints(points, lang), fmt: (n) => formatCheckPoints(n, lang) };
};

/**
 * Contenido del tooltip "Cómo se cobra". `auto`: agrega qué pasa sin puntos
 * en la consulta automática; `intro`: una línea antes (p. ej. qué hace el
 * módulo).
 */
export const BackgroundCheckChargesTip = ({ points = BACKGROUND_CHECK_POINTS, auto = false, intro = null }) => {
  const { t } = useTranslation();
  const { points: pts } = useCheckPoints(points);
  return (
    <Box sx={{ p: 0.5 }}>
      {intro && <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>{intro}</Typography>}
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>{t('ats.backgroundCheckPricing.title')}</Typography>
      <Typography variant="caption" component="div">• {t('ats.backgroundCheckPricing.charge', { points: pts })}</Typography>
      <Typography variant="caption" component="div">• {t('ats.backgroundCheckPricing.free')}</Typography>
      <Typography variant="caption" component="div">
        • {t(auto ? 'ats.backgroundCheckPricing.noPointsAuto' : 'ats.backgroundCheckPricing.noPoints')}
      </Typography>
    </Box>
  );
};

// Dentro de un <label> (switch, casilla) el clic en el ícono no debe
// cambiar el control: solo muestra el tooltip
const stopLabelClick = (e) => {
  e.preventDefault();
  e.stopPropagation();
};

/** Solo el ícono (i), para ponerlo dentro de un elemento que ya tiene su tooltip */
export const CostInfoGlyph = ({ sx = {} }) => (
  <InfoIcon
    onClick={stopLabelClick}
    sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help', verticalAlign: 'middle', ml: 0.5, ...sx }}
  />
);

/** Ícono (i) con el tooltip "Cómo se cobra" */
export const BackgroundCheckCostInfo = ({ auto = false, intro = null, points = BACKGROUND_CHECK_POINTS, sx = {} }) => {
  const { t } = useTranslation();
  return (
    <Tooltip title={<BackgroundCheckChargesTip auto={auto} intro={intro} points={points} />} arrow placement="top">
      <InfoIcon
        titleAccess={t('ats.backgroundCheckPricing.title')}
        onClick={stopLabelClick}
        sx={{ fontSize: 15, color: 'text.secondary', cursor: 'help', verticalAlign: 'middle', ml: 0.5, ...sx }}
      />
    </Tooltip>
  );
};

const POINTS_CODES = ['INSUFFICIENT_POINTS', 'SERVICE_NOT_AVAILABLE', 'NO_POINTS_SYSTEM'];

/**
 * Texto de un bloqueo por puntos: 403 { errorType: 'POINTS', code, required,
 * remaining } del backend, o `points` del resumen del modal.
 */
export const pointsBlockMessage = (t, block) => {
  const fmt = (n) => formatCheckPoints(n);
  switch (block && block.code) {
    case 'INSUFFICIENT_POINTS':
      return t('ats.backgroundCheckPricing.errors.insufficient', {
        required: fmt(block.required ?? BACKGROUND_CHECK_POINTS),
        remaining: formatPointsBalance(Math.max(0, block.remaining || 0)),
      });
    case 'SERVICE_NOT_AVAILABLE': return t('ats.backgroundCheckPricing.errors.serviceNotAvailable');
    case 'NO_POINTS_SYSTEM': return t('ats.backgroundCheckPricing.errors.noPointsSystem');
    default: return t('ats.backgroundCheckPricing.errors.unavailable');
  }
};

/** Si el error es un bloqueo por puntos, { code, required, remaining }; si no, null */
export const pointsBlockFromError = (err) => {
  const payload = err && err.response && err.response.data && err.response.data.response;
  if (err && err.response && err.response.status === 403 && payload && payload.errorType === 'POINTS') {
    return { code: payload.code || null, required: payload.required ?? null, remaining: payload.remaining ?? null };
  }
  return null;
};

/**
 * Aviso después de mover tarjetas a una etapa con consulta automática:
 * a cuántas no se les consultó por puntos (backgroundCheckSkipped del
 * backend). null si a ninguna.
 */
export const skippedAfterMoveMessage = (t, skippedList = []) => {
  const list = (skippedList || []).filter(Boolean);
  if (list.length === 0) return null;
  const first = list[0];
  const fmt = (n) => formatCheckPoints(n);
  const key = POINTS_CODES.includes(first.code) ? first.code : 'default';
  const reason = t(`ats.backgroundCheckPricing.autoSkipped.${key}`, {
    required: fmt(first.required ?? BACKGROUND_CHECK_POINTS),
    remaining: formatPointsBalance(Math.max(0, first.remaining || 0)),
  });
  return t('ats.backgroundCheckPricing.movedSkipped', { count: list.length, reason });
};
